import { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

import { AppDispatch } from '../../store';
import { loadSession } from '../../store/slices/sessionSlice';
import { decodeSessionFromStorage } from '../services/userSessionService';
import { extractSsoPayload, decryptSsoUsername } from '../services/ssoService';
import { fetchOAuthKey, isAuthKeyExpired } from '../services/oauthService';
import { useSsoLoginMutation } from '../../store/api/authService';

export type SsoStatus = 'idle' | 'processing' | 'done' | 'error';

/**
 * Option-1 SSO bootstrap: when the app is opened via the Angular handoff URL
 * (`/<type><cipher>` or `?userLogin=<cipher>`), decrypt the username, exchange
 * it for a real session token, hydrate the session, and route to the dashboard.
 *
 * When no SSO payload is present it stays 'idle' and the normal login flow runs.
 */
export function useSsoBootstrap(): SsoStatus {
  const [status, setStatus] = useState<SsoStatus>('idle');
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const [ssoLogin] = useSsoLoginMutation();
  const startedRef = useRef(false);

  useEffect(() => {
    // Guard against React StrictMode's double-invoke and re-renders so the
    // token exchange fires exactly once.
    if (startedRef.current) return;

    const payload = extractSsoPayload();
    if (!payload) return;

    startedRef.current = true;
    setStatus('processing');

    (async () => {
      try {
        const username = decryptSsoUsername(payload.cipher);
        if (!username) {
          throw new Error('Could not decrypt SSO username');
        }

        // Ensure a VALID OAuth key exists so baseQuery attaches the
        // Authorization header on the token request. Refetch when the key is
        // missing OR expired — a stale key would make ssoLogin 401 and the
        // auto-login silently fail. A still-valid key is left untouched.
        if (isAuthKeyExpired()) {
          await fetchOAuthKey();
        }

        const res = await ssoLogin({
          emis_username: username,
          type: Number(payload.type) || 2,
        }).unwrap();
        const token = res?.records?.token;
        if (!res?.dataStatus || !token) {
          throw new Error(res?.message || 'SSO login was rejected');
        }

        // Match the Angular login: store both keys, then hydrate from storage
        // via the shared session decoder.
        localStorage.setItem('token', token);
        localStorage.setItem('user_token', token);

        const session = decodeSessionFromStorage();
        if (session) dispatch(loadSession(session));

        setStatus('done');
        navigate('/dashboard', { replace: true });
      } catch (err) {
        console.error('[SSO] bootstrap failed:', err);
        toast.error('Single sign-on failed. Please log in.');
        setStatus('error');
        navigate('/', { replace: true });
      }
    })();
  }, [dispatch, navigate, ssoLogin]);

  return status;
}
